import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAuthSession, adminApi } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';

const AdminVerificationsPage = () => {
  const navigate = useNavigate();
  const authSession = getAuthSession();
  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchPending = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminApi.pendingVerifications();
      const raw = res.profiles || res.pending || res.therapists || res.data || [];
      const mapped = raw.map(t => {
        const u = t.userId || t.user || {};
        return {
          _id: t._id || t.id,
          name: u.name || t.name || 'Therapist',
          email: u.email || t.email || 'N/A',
          phone: u.phone || t.phone || 'N/A',
          specialization: t.specialization || '',
          location: t.location || 'N/A',
          experience: t.yearsOfExperience || 0,
          documents: t.documents || [],
          status: t.verificationStatus || 'pending',
          date: t.createdAt ? new Date(t.createdAt).toISOString().split('T')[0] : 'N/A'
        };
      });
      setTherapists(mapped);
    } catch (err) {
      console.error('Failed to fetch pending verifications:', err);
      setError(err.message || 'Failed to load verifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authSession?.token || authSession.user?.role !== 'admin') {
      navigate('/admin-login');
      return;
    }
    fetchPending();
  }, [authSession?.token, navigate]);

  const handleAction = async (id, decision) => {
    try {
      await adminApi.reviewVerification(id, { decision });
      setTherapists(therapists.filter(t => t._id !== id));
    } catch (err) {
      alert(err.message || `Failed to ${decision} application`);
    }
  };

  return (
    <>
      <DashboardSidebar userRole="admin" />
      <div style={{ minHeight: '100vh', background: '#f8f9fa', padding: '40px 20px', marginLeft: '280px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <Link to="/admin-dashboard" style={{ color: '#667eea', textDecoration: 'none', marginBottom: '20px', display: 'inline-block' }}>
            ← Back to Dashboard
          </Link>
          <h1 style={{ fontSize: '32px', color: '#333', margin: '20px 0 10px 0' }}>Pending Practitioner Verifications</h1>
          <p style={{ color: '#666', marginBottom: '30px' }}>Review and verify therapist applications & uploaded credential documents</p>

          {error && (
            <div style={{ padding: '12px 16px', background: '#fee2e2', color: '#991b1b', borderRadius: '8px', marginBottom: '20px' }}>
              {error}
            </div>
          )}

          <div style={{ background: 'white', borderRadius: '12px', padding: '30px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
            {loading ? (
              <p style={{ textAlign: 'center', color: '#666', padding: '30px' }}>Loading pending verifications...</p>
            ) : therapists.length > 0 ? (
              <div style={{ display: 'grid', gap: '20px' }}>
                {therapists.map((therapist) => (
                  <div key={therapist._id} style={{ padding: '24px', border: '1px solid #e5e7eb', borderRadius: '10px', background: '#fff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                      <div>
                        <h3 style={{ margin: '0 0 6px 0', fontSize: '20px', color: '#111827' }}>👨‍⚕️ {therapist.name}</h3>
                        <p style={{ margin: '0 0 4px 0', color: '#4b5563', fontSize: '14px' }}>
                          📧 <strong>Email:</strong> {therapist.email} | 📞 <strong>Phone:</strong> {therapist.phone}
                        </p>
                        <p style={{ margin: 0, color: '#4b5563', fontSize: '14px' }}>
                          🩺 <strong>Specialization:</strong> {therapist.specialization} | 📍 <strong>Location:</strong> {therapist.location} | ⏳ <strong>Experience:</strong> {therapist.experience} yrs
                        </p>
                      </div>

                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                          onClick={() => handleAction(therapist._id, 'approved')}
                          style={{ padding: '10px 20px', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}
                        >
                          ✓ Approve Documents
                        </button>
                        <button
                          onClick={() => handleAction(therapist._id, 'rejected')}
                          style={{ padding: '10px 20px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}
                        >
                          ✗ Reject
                        </button>
                      </div>
                    </div>

                    {/* Verification Documents */}
                    <div style={{ background: '#f9fafb', padding: '16px', borderRadius: '8px', marginTop: '15px' }}>
                      <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        📜 Uploaded Credentials ({therapist.documents.length})
                      </h4>
                      {therapist.documents.length > 0 ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '10px' }}>
                          {therapist.documents.map((doc, idx) => (
                            <div key={doc._id || idx} style={{ padding: '10px 14px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <div style={{ fontWeight: 600, fontSize: '13px', color: '#1f2937' }}>📄 {doc.documentType}</div>
                                {doc.originalName && <div style={{ fontSize: '11px', color: '#6b7280' }}>{doc.originalName}</div>}
                              </div>
                              {doc.fileUrl && (
                                <a href={doc.fileUrl} target="_blank" rel="noreferrer" style={{ padding: '4px 10px', background: '#eef2ff', color: '#4f46e5', borderRadius: '4px', textDecoration: 'none', fontSize: '12px', fontWeight: 600 }}>
                                  View File 👁️
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p style={{ margin: 0, color: '#9ca3af', fontSize: '13px', fontStyle: 'italic' }}>
                          No verification documents uploaded yet.
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ textAlign: 'center', color: '#999', padding: '40px' }}>No pending verifications</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminVerificationsPage;
